// Admin data layer - cross-tenant reads + impersonation.
// All reads rely on the super-admin RLS policies from migration 0002/0010.

import { supabase } from '@govern360/shared';

/* ---- Platform overview / tenants ---- */
/* g360-k345 - per-client licence and usage summary for the MSP view.
   Reads msp_license_summary(), which separates four numbers that are
   routinely conflated: allocated, assigned, active endpoints, dormant. */
export async function listLicenseSummary() {
  const { data, error } = await supabase.rpc('msp_license_summary');
  return { data: data || [], error };
}

export async function listTenants() {
  const { data, error } = await supabase
    .from('organizations')
    .select('id, slug, name, industry, size, plan, subscription_status, seats, status, created_at, stripe_customer_id')
    .order('created_at', { ascending: false });
  return { data: data || [], error };
}

export async function tenantStats() {
  // Cross-tenant counts. RLS lets super-admins read all rows.
  // G360_K462 - a failed count is null (shown as ?), never 0; and "Open
  // incidents" counts open ones - it counted every incident ever raised.
  const [tools, agents, incidents] = await Promise.all([
    supabase.from('ai_tools').select('id', { count: 'exact', head: true }),
    supabase.from('agents').select('id', { count: 'exact', head: true }),
    supabase.from('incidents').select('id', { count: 'exact', head: true }).in('status', ['open', 'investigating', 'contained']),
  ]);
  const n = (r) => (r && !r.error && typeof r.count === 'number' ? r.count : null);
  return { tools: n(tools), agents: n(agents), incidents: n(incidents) };
}

/* ---- Staff ---- */
export async function listStaff() {
  // staff_roles has no FK to profiles, so a PostgREST embed (profile:user_id(...))
  // cannot resolve and errors -> empty list. Use the list_staff() RPC, which joins
  // profiles + auth.users server-side (security definer, gated on is_staff()).
  const { data, error } = await supabase.rpc('list_staff');
  const rows = (data || []).map((r) => ({
    user_id: r.user_id,
    role: r.role,
    department: r.department,
    created_at: r.created_at,
    email: r.email,
    profile: { full_name: r.full_name },
  }));
  return { data: rows, error };
}

/* ---- Staff management (super-admin only; server-side guarded + audited) ---- */
export async function setStaffRole(userId, role) {
  const { error } = await supabase.rpc('set_staff_role', { p_user_id: userId, p_role: role });
  return { error };
}
export async function removeStaff(userId) {
  const { error } = await supabase.rpc('remove_staff', { p_user_id: userId });
  return { error };
}
export async function inviteStaff(email, role, department) {
  const { error } = await supabase.rpc('invite_staff', { p_email: email, p_role: role, p_department: department || null });
  return { error };
}
export async function listPendingStaff() {
  const { data, error } = await supabase.rpc('list_pending_staff');
  return { data: data || [], error };
}
export async function cancelStaffInvitation(id) {
  const { error } = await supabase.rpc('cancel_staff_invitation', { p_id: id });
  return { error };
}

/* ---- Feature flags ---- */
export async function listFlags() {
  const { data, error } = await supabase.from('feature_flags').select('*').order('key');
  return { data: data || [], error };
}
export async function setFlag(id, enabled) {
  // G360_K462 - RLS turns a refused update into zero rows and no error.
  const { data, error } = await supabase.from('feature_flags')
    .update({ enabled, updated_at: new Date().toISOString() }).eq('id', id).select('id');
  if (!error && (!Array.isArray(data) || data.length === 0)) return { error: { message: 'the change was not applied (no permission)' } };
  return { error };
}

/* ---- Global detectors (the catalog) ---- */
export async function listDetectorCatalog() {
  const { data, error } = await supabase
    .from('detector_catalog').select('*').order('category').order('name');
  return { data: data || [], error };
}

/* ---- Model advisories (G360_K516) ----
   Staff-kept, platform-wide. A refused write comes back as zero rows and no
   error under row-level security, so it is reported as refused. */
export async function listModelAdvisories() {
  const { data, error } = await supabase.from('model_advisory').select('*')
    .order('status').order('updated_at', { ascending: false });
  return { data: data || [], error };
}
export async function saveModelAdvisory(row) {
  const body = Object.assign({}, row);
  delete body.id;
  const q = row.id
    ? supabase.from('model_advisory').update(body).eq('id', row.id)
    : supabase.from('model_advisory').insert([body]);
  const { data, error } = await q.select('id');
  if (!error && (!Array.isArray(data) || data.length === 0)) return { error: { message: 'the change was not applied (no permission)' } };
  return { error };
}

/* ---- Cross-tenant audit ---- */
export async function listAdminAudit(limit = 100) {
  const { data, error } = await supabase
    .from('admin_audit_events').select('*')
    .order('created_at', { ascending: false }).limit(limit);
  return { data: data || [], error };
}

/* ---- Impersonation ---- */
export async function listActiveGrants() {
  const { data, error } = await supabase
    .from('impersonation_grants').select('*')
    .is('revoked_at', null).gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false });
  return { data: data || [], error };
}

// Mint a grant via RPC (re-checks staff server-side), then return the URL the
// staff member should open: the console with the grant token.
export async function startImpersonation(tenantId, reason, consoleUrl) {
  const { data, error } = await supabase.rpc('mint_impersonation_grant', {
    p_tenant_id: tenantId, p_reason: reason, p_minutes: 30,
  });
  if (error) return { error };
  const url = `${consoleUrl}/?impersonate=${tenantId}&grant=${data.token}`;
  return { grant: data, url };
}

export async function revokeGrant(id) {
  const { error } = await supabase.rpc('revoke_impersonation_grant', { p_id: id });
  return { error };
}

export async function seedAdminDemo() {
  const { error } = await supabase.rpc('seed_admin_demo');
  return { error };
}

/* ---- UI constants ---- */
export const STAFF_ROLE_LABEL = {
  super_admin: 'Super admin', support: 'Support', customer_success: 'Customer success',
  sales_engineer: 'Sales engineer', security: 'Security',
};
export const STAFF_ROLES = ['super_admin', 'support', 'customer_success', 'sales_engineer', 'security'];
